import * as DropdownPrimitive from '@radix-ui/react-dropdown-menu'
import * as PopoverPrimitive from '@radix-ui/react-popover'
import * as TabsPrimitive from '@radix-ui/react-tabs'
import * as TooltipPrimitive from '@radix-ui/react-tooltip'
import type { ReactNode } from 'react'

import { cn } from '@/lib/utils'

const floating = 'z-50 animate-pop rounded-[12px] border border-line bg-surface shadow-pop'

// ---------------------------------------------------------------- dropdown menu
export const Menu = DropdownPrimitive.Root
export const MenuTrigger = DropdownPrimitive.Trigger

export function MenuContent({
  children,
  align = 'end',
  className,
}: {
  children: ReactNode
  align?: 'start' | 'end' | 'center'
  className?: string
}) {
  return (
    <DropdownPrimitive.Portal>
      <DropdownPrimitive.Content
        align={align}
        sideOffset={6}
        className={cn(floating, 'min-w-44 p-1', className)}
      >
        {children}
      </DropdownPrimitive.Content>
    </DropdownPrimitive.Portal>
  )
}

export function MenuItem({
  children,
  onSelect,
  danger,
  disabled,
  icon,
}: {
  children: ReactNode
  onSelect?: () => void
  danger?: boolean
  disabled?: boolean
  icon?: ReactNode
}) {
  return (
    <DropdownPrimitive.Item
      onSelect={onSelect}
      disabled={disabled}
      className={cn(
        'flex cursor-pointer items-center gap-2.5 rounded-[8px] px-2.5 py-1.5 text-[0.8125rem] outline-none select-none data-[disabled]:cursor-default data-[disabled]:opacity-50 [&_svg]:size-4',
        danger
          ? 'text-danger-ink data-[highlighted]:bg-danger-soft'
          : 'text-ink data-[highlighted]:bg-surface-3',
      )}
    >
      {icon && <span className={cn('flex', danger ? 'text-danger-ink' : 'text-ink-3')}>{icon}</span>}
      {children}
    </DropdownPrimitive.Item>
  )
}

export function MenuLabel({ children }: { children: ReactNode }) {
  return (
    <DropdownPrimitive.Label className="px-2.5 pt-1.5 pb-1 text-2xs text-ink-3">
      {children}
    </DropdownPrimitive.Label>
  )
}

export function MenuSeparator() {
  return <DropdownPrimitive.Separator className="my-1 h-px bg-line" />
}

// ---------------------------------------------------------------- tooltip
export const TooltipProvider = TooltipPrimitive.Provider

export function Tooltip({
  content,
  children,
  side = 'top',
}: {
  content: ReactNode
  children: ReactNode
  side?: 'top' | 'right' | 'bottom' | 'left'
}) {
  return (
    <TooltipPrimitive.Root>
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          side={side}
          sideOffset={6}
          className="z-50 max-w-64 animate-fade rounded-[7px] bg-[#101b33] px-2.5 py-1.5 text-xs text-white shadow-pop"
        >
          {content}
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  )
}

// ---------------------------------------------------------------- popover
export const Popover = PopoverPrimitive.Root
export const PopoverTrigger = PopoverPrimitive.Trigger

export function PopoverContent({
  children,
  className,
  align = 'end',
}: {
  children: ReactNode
  className?: string
  align?: 'start' | 'end' | 'center'
}) {
  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        align={align}
        sideOffset={8}
        className={cn(floating, 'outline-none', className)}
      >
        {children}
      </PopoverPrimitive.Content>
    </PopoverPrimitive.Portal>
  )
}

// ---------------------------------------------------------------- tabs (underline style)
export const Tabs = TabsPrimitive.Root

export function TabList({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <TabsPrimitive.List className={cn('flex gap-5 overflow-x-auto border-b border-line', className)}>
      {children}
    </TabsPrimitive.List>
  )
}

export function Tab({ value, children }: { value: string; children: ReactNode }) {
  return (
    <TabsPrimitive.Trigger
      value={value}
      className="relative -mb-px flex h-10 cursor-pointer items-center gap-2 border-b-2 border-transparent text-[0.8125rem] font-medium whitespace-nowrap text-ink-3 transition-colors hover:text-ink data-[state=active]:border-primary data-[state=active]:text-ink"
    >
      {children}
    </TabsPrimitive.Trigger>
  )
}

export const TabPanel = TabsPrimitive.Content
